import React, { useEffect, useState, useMemo } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Skeleton,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { dashboardService } from "../services/dashboardService";
import { teamServices } from "../services/teamServices";

const monthLabels = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const generateMonthYearOptions = (monthsBack = 6) => {
    const now = new Date();
    const opts = [];
    for (let i = 0; i < monthsBack; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const y = d.getFullYear();
        const m = d.getMonth() + 1; // 1..12
        const label = `${monthLabels[d.getMonth()]} ${y}`;
        const value = `${y}-${String(m).padStart(2, "0")}`; // "2025-02"
        opts.push({ value, label, month: m, year: y });
    }
    return opts;
};

const MonthlyTargetHoursChart = ({ roles }) => {
    const isAdminOrSubAdmin = roles?.includes("SUPER_ADMIN") || roles?.includes("ADMIN") || roles?.includes("SUB_ADMIN");
    const [teams, setTeams] = useState([]);
    const [selectedTeam, setSelectedTeam] = useState('');

    const monthOptions = useMemo(() => generateMonthYearOptions(6), []);
    const defaultMonthValue = monthOptions.length ? monthOptions[0].value : (() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    })();

    const [selectedMonthYear, setSelectedMonthYear] = useState(defaultMonthValue);
    const [selectedMonth, selectedYear] = useMemo(() => {
        const parts = (selectedMonthYear || defaultMonthValue).split("-");
        const y = Number(parts[0]);
        const m = Number(parts[1]);
        return [m, y];
    }, [selectedMonthYear, defaultMonthValue]);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!isAdminOrSubAdmin) {
            const userId = localStorage.getItem("userId");
            if (userId) {
                setSelectedTeam(userId);
            }
        }
    }, [isAdminOrSubAdmin]);

    // Fetch teams for admin/sub-admin
    useEffect(() => {
        if (isAdminOrSubAdmin) {
            const fetchTeams = async () => {
                try {
                    const list = await teamServices.getAllTeamsListForUserScreen();
                    setTeams(list || []);
                    if (list && list.length > 0) {
                        const firstOwnerId = typeof list[0].ownerId === 'object' ? list[0].ownerId?.id : list[0].ownerId;
                        setSelectedTeam(firstOwnerId || '');
                    }
                } catch (err) {
                    console.error("Failed to fetch teams", err);
                }
            };
            fetchTeams();
        }
    }, [isAdminOrSubAdmin]);

    // Fetch user target achieved hours data
    useEffect(() => {
        if (!selectedTeam) return;

        const fetchData = async () => {
            try {
                setLoading(true);
                const result = await dashboardService.getUserTargetAchievement(selectedTeam, selectedMonth, selectedYear);
                setData(result || []);
            } catch (err) {
                console.error("Failed to fetch user target achievement", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedTeam, selectedMonth, selectedYear]);

    const formatChargeHours = (hoursDecimal) => {
        if (hoursDecimal == null) hoursDecimal = 0.0;
        let h = Math.floor(hoursDecimal);
        let m = Math.round((hoursDecimal - h) * 60);

        if (m === 60) {
            h += 1;
            m = 0;
        }
        return `${h}:${String(m).padStart(2, '0')}h`;
    };

    // Prepare BarChart data
    const chartData = useMemo(() => {
        return data.map((d) => ({
            id: d.userId,
            label: d.userName,
            value: d.achievedPercentage,
            achievedHours: d.achievedHours,
            targetHours: d.monthlyTargetHours,
        }));
    }, [data]);

    const xLabels = useMemo(() => chartData.map((d) => d.label), [chartData]);

    const underTargetSeries = [];
    const onTargetSeries = [];
    const overTargetSeries = [];

    chartData.forEach((d) => {
        if (d.value < 100) {
            underTargetSeries.push(d.value);
            onTargetSeries.push(0);
            overTargetSeries.push(0);
        } else if (d.value === 100) {
            underTargetSeries.push(0);
            onTargetSeries.push(d.value);
            overTargetSeries.push(0);
        } else {
            underTargetSeries.push(0);
            onTargetSeries.push(0);
            overTargetSeries.push(d.value);
        }
    });

    const seriesValueFormatter = (value, { dataIndex }) => {
        if (value === 0 || value == null) return null; // Don't show tooltip for empty bars
        const user = chartData[dataIndex];
        if (!user) return '';
        return `${formatChargeHours(user.achievedHours)} / ${user.targetHours}h (${user.value}%)`;
    };

    const barValues = useMemo(() => chartData.map((d) => Number(d.value) || 0), [chartData]);

    const barColors = useMemo(() =>
        chartData.map((d) => {
            if (d.value < 100) return "#ef5350";
            if (d.value === 100) return "#42a5f5";
            return "#66bb6a";
        }), [chartData]
    );

    return (
        <Box sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Team Dropdown */}
            <Stack
                                direction="row"
                spacing={1}
                sx={{ mb: 2 }}
                justifyContent="center"
                alignItems="center"
            >
                {isAdminOrSubAdmin && (
                    <FormControl sx={{ flex: 1, minWidth: 120 }} size="small">
                        <InputLabel>Select Team</InputLabel>
                        <Select
                            value={(Array.isArray(teams) && teams.some((t) => {
                                const ownerIdVal = typeof t.ownerId === 'object' ? t.ownerId?.id : t.ownerId;
                                return String(ownerIdVal) === String(selectedTeam);
                            })) ? selectedTeam : ''}
                            label="Select Team"
                            onChange={(e) => setSelectedTeam(e.target.value)}
                        >
                            {teams.map((t) => {
                                const ownerIdVal = typeof t.ownerId === 'object' ? t.ownerId?.id : t.ownerId;
                                return (
                                    <MenuItem key={t.id} value={ownerIdVal}>
                                        {t.name}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                )}

                <FormControl sx={{ flex: 1, minWidth: 120 }} size="small">
                    <InputLabel id="month-year-select-label">Select Month</InputLabel>
                    <Select
                        labelId="month-year-select-label"
                        value={(Array.isArray(monthOptions) && monthOptions.some((opt) => opt.value === selectedMonthYear)) ? selectedMonthYear : ''}
                        label="Select Month"
                        onChange={(e) => setSelectedMonthYear(e.target.value)}
                    >
                        {monthOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            <Box className="chart-container">
                {loading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 6,
                            gap: 2,
                        }}
                    >
                        <Skeleton variant="rectangular" width="100%" height={240} sx={{ borderRadius: 1 }} />
                    </Box>
                ) : chartData.length === 0 ? (
                    <Typography
                        align="center"
                        color="text.secondary"
                        sx={{
                            height: 250,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        No data available
                    </Typography>
                ) : (
                    <Box className="chart-pie-box">
                        <BarChart
                            series={[
                                {
                                    data: underTargetSeries,
                                    color: "#ef5350", // Red
                                    stack: "total",
                                    valueFormatter: seriesValueFormatter
                                },
                                {
                                    data: onTargetSeries,
                                    color: "#42a5f5", // Blue
                                    stack: "total",
                                    valueFormatter: seriesValueFormatter
                                },
                                {
                                    data: overTargetSeries,
                                    color: "#66bb6a", // Green
                                    stack: "total",
                                    valueFormatter: seriesValueFormatter
                                },
                            ]}
                            xAxis={[{ data: xLabels, scaleType: "band", label: "Users" }]}
                            yAxis={[{
                                label: "Target Achieved (%)",
                                valueFormatter: (value) => `${Math.round(value)}%`
                            }]}
                            slotProps={{ bar: { rx: 4 }, }}
                            margin={{ left: 120, right: 30, top: 30, bottom: 70 }}
                            grid={{ vertical: true, horizontal: true }}
                            sx={{
                                width: "100%",
                                height: 350,
                                '& .MuiChartsAxis-left .MuiChartsAxis-label': {
                                    transform: 'translateX(-1.5625rem)',
                                }
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default MonthlyTargetHoursChart;